import React, { useState, useEffect } from 'react';
import Header from '../Header';
import { vendorAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { notify, confirm } from '../../utils/notify';
import { useListControls, ListToolbar, Pagination } from '../../components/ListControls';
import Topbar from "../../components/Topbar";

const money = n => new Intl.NumberFormat('en-IN',{style:'currency',currency:'INR',maximumFractionDigits:0}).format(Number(n)||0);
const L_STATUS = {
  PENDING_L1: { label:'Pending L1 (Reporting Mgr)', role:'manager' },
  PENDING_L2: { label:'Pending L2 (Project Mgr)',   role:'manager' },
  PENDING_L3: { label:'Pending L3 (HOD)',            role:'hod' },
};

function ApprovalDots({ status }) {
  const steps = [
    { l:'L1\nReporting Mgr', done:['PENDING_L2','PENDING_L3','PENDING_FINANCE','FINANCE_APPROVED','PAID'].includes(status), active:status==='PENDING_L1' },
    { l:'L2\nProject Mgr',   done:['PENDING_L3','PENDING_FINANCE','FINANCE_APPROVED','PAID'].includes(status), active:status==='PENDING_L2' },
    { l:'L3\nHOD',           done:['PENDING_FINANCE','FINANCE_APPROVED','PAID'].includes(status), active:status==='PENDING_L3' },
    { l:'Finance',           done:['FINANCE_APPROVED','PAID'].includes(status), active:status==='PENDING_FINANCE' },
  ];
  return (
    <div className="d-flex align-items-center mt-2">
      {steps.map((s,i) => (
        <div key={i} className="d-flex align-items-center" style={{flex:1}}>
          <div className="d-flex flex-column align-items-center">
            <div style={{width:24,height:24,borderRadius:'50%',background:s.done?'#22c55e':s.active?'#4f6ef7':'#f3f4f6',color:s.done||s.active?'#fff':'#aaa',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.552rem',fontWeight:700}}>
              {s.done ? <i className="bi bi-check"/> : i+1}
            </div>
            <div style={{fontSize:'0.46rem',color:s.done||s.active?'#374151':'#aaa',textAlign:'center',marginTop:2,width:39,whiteSpace:'pre-line',lineHeight:1.3}}>{s.l}</div>
          </div>
          {i < 3 && <div style={{flex:1,height:2,background:s.done?'#22c55e':'#e5e7eb',marginBottom:8}}/>}
        </div>
      ))}
    </div>
  );
}

function PaymentApprovals() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = () => {
    setLoading(true);
    vendorAPI.listPaymentRequests({ limit: 200 }).then(d => {
      setRequests((d.requests||[]).filter(r => ['PENDING_L1','PENDING_L2','PENDING_L3'].includes(r.status)));
    }).catch(() => notify.error('Failed to load')).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const canApprove = (r) => {
    if (!user) return false;
    // Server decides (assigned approver / delegate / role pool / admin)
    if (typeof r.canAct === 'boolean') return r.canAct;
    if (user.role === 'admin') return true;
    const info = L_STATUS[r.status];
    return info && user.role === info.role;
  };

  const approve = async (r) => {
    const res = await confirm.action(`Approve payment for ${r.vendorId?.name}?`, `Amount: ${money(r.requestedAmount)}`, 'Yes, Approve', '#16a34a');
    if (!res.isConfirmed) return;
    setActing(r._id);
    try { await vendorAPI.approvePaymentRequest(r._id, { remarks: 'Approved' }); notify.success('Payment request approved!'); await load(); }
    catch (e) { notify.error(e.message || 'Failed'); } finally { setActing(null); }
  };

  const reject = async (r) => {
    const res = await confirm.reject(`Reject payment request for ${r.vendorId?.name}?`);
    if (!res.isConfirmed) return;
    setActing(r._id);
    try { await vendorAPI.rejectPaymentRequest(r._id, { remarks: res.value }); notify.success('Request rejected'); await load(); }
    catch (e) { notify.error(e.message || 'Failed'); } finally { setActing(null); }
  };

  const lc = useListControls(requests, {
    searchFields: [r=>r.vendorId?.name, 'paymentRequestCode', 'poNumber', r=>r.projectId?.projectCode],
    dateField: 'createdAt',
    pageSize: 10,
  });

  return (
    <>
      <Header/>
      <div id="db-wrapper"><main id="page-content">
          <Topbar section="Vendor Payment" /><section className="container-fluid p-4 pt-3">
        <div className="page-hero d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div><h5 className="text-white lh-1">L3 Payment Approvals</h5><div className="sub">Sequential L1 → L2 → L3 (HOD) approval flow for vendor payment requests</div></div>
          <button onClick={load} className="btn fw-semibold" style={{background:'rgba(255,255,255,0.2)',color:'#fff',borderRadius:8,border:'1px solid rgba(255,255,255,0.3)',padding:'6px 13px'}}>
            <i className="bi bi-arrow-clockwise me-1"/>Refresh
          </button>
        </div>

        <div className="row g-2 mb-3">
          {[{label:'Pending L1',val:requests.filter(r=>r.status==='PENDING_L1').length,bg:'#fffbeb',ic:'#f59e0b'},{label:'Pending L2',val:requests.filter(r=>r.status==='PENDING_L2').length,bg:'#eff6ff',ic:'#3b82f6'},{label:'Pending L3 (HOD)',val:requests.filter(r=>r.status==='PENDING_L3').length,bg:'#f3e8ff',ic:'#7c3aed'},{label:'Total Pending',val:requests.length,bg:'#f0fdf4',ic:'#22c55e'}].map((s,i)=>(
            <div key={i} className="col-6 col-md-3">
              <div className="kpi-card d-flex align-items-center gap-2">
                <div className="kpi-icon" style={{background:s.bg}}><i className="bi bi-hourglass" style={{color:s.ic}}/></div>
                <div><div style={{fontSize:'1.17rem',fontWeight:700,lineHeight:1}}>{s.val}</div><div style={{fontSize:'0.7rem',color:'#6b7280',marginTop:2}}>{s.label}</div></div>
              </div>
            </div>
          ))}
        </div>

        {loading ? <div className="text-center py-5"><span className="spinner-border text-primary"/></div>
        : requests.length===0 ? <div className="empty-state"><i className="bi bi-inbox"/><p>No payment requests pending approval</p></div>
        : (<>
        <ListToolbar search={lc.search} setSearch={lc.setSearch} dateFrom={lc.dateFrom} setDateFrom={lc.setDateFrom} dateTo={lc.dateTo} setDateTo={lc.setDateTo} placeholder="Search by vendor, code, PO number..." dateLabel="Submitted"/>
        {lc.paginated.map((r,idx) => {
          const info = L_STATUS[r.status] || {};
          return (
            <div key={r._id} className="record-card">
              <div className="record-number">{(lc.page-1)*lc.pageSize+idx+1}.</div>
              <div className="record-body">
                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                  <div style={{fontWeight:700,fontSize:'0.874rem',color:'#111827'}}>{r.vendorId?.name||'—'}</div>
                  <span className="status-pill status-pending"><i className="bi bi-hourglass"/>{info.label||r.status}</span>
                  <span style={{background:'#f0fdf4',color:'#16a34a',fontSize:'0.7rem',fontWeight:600,padding:'2px 6px',borderRadius:12}}>{money(r.requestedAmount)}</span>
                </div>
                <div className="row g-2">
                  <div className="col-12 col-md-5">
                    <div className="record-field"><span className="rf-label">Code — </span><span className="rf-value text-primary">{r.paymentRequestCode}</span></div>
                    <div className="record-field"><span className="rf-label">Project — </span><span className="rf-value">{r.projectId?.projectCode} — {r.projectId?.name}</span></div>
                    <div className="record-field"><span className="rf-label">PO — </span><span className="rf-value">{r.poNumber||'—'}</span></div>
                    <div className="record-field"><span className="rf-label">Cost Center — </span><span className="rf-value">{r.costCenter}</span></div>
                    <div className="record-field"><span className="rf-label">Terms — </span><span className="rf-value">{r.paymentTerms}</span></div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div style={{fontSize:'0.7rem',color:'#9ca3af',fontWeight:600,marginBottom:4}}>APPROVAL PROGRESS</div>
                    <ApprovalDots status={r.status}/>
                    {r.approvalHistory?.length > 0 && (
                      <div className="mt-2 d-flex gap-1 flex-wrap">
                        {r.approvalHistory.map((h,i) => <span key={i} style={{fontSize:'0.598rem',padding:'1px 5px',borderRadius:12,background:'#f0fdf4',color:'#16a34a',border:'1px solid #bbf7d0'}}>{h.level} <i className="bi bi-check"/>{h.approverId?.userName||'Approved'}</span>)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {canApprove(r) && <><div className="divider d-none d-md-block"/>
              <div className="record-actions">
                <button className="btn-card-approve" onClick={() => approve(r)} disabled={acting===r._id}>
                  {acting===r._id ? <span className="spinner-border spinner-border-sm"/> : 'Approve'}
                </button>
                <div className="btn-divider"/>
                <button className="btn-card-reject" onClick={() => reject(r)} disabled={acting===r._id}>Reject</button>
              </div></>}
            </div>
          );
        })}
        <Pagination page={lc.page} totalPages={lc.totalPages} setPage={lc.setPage} totalCount={lc.totalCount} pageSize={lc.pageSize} itemLabel="payment requests"/>
        </>)}
      </section></main></div>
    </>
  );
}
export default PaymentApprovals;
